import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { resolveSteamId } from '../lib/api/scanApi';
import { getStats } from '../lib/api/statsApi';
import { startScan } from '../lib/actions/scanActions';
import { storeSteamId } from '../lib/api/billingApi';
import { trackEvent } from '../lib/api/trackApi';
import Reviews from './Reviews';

// Formatte un compteur (ex: 1200 -> "1.2K"), ou "…" tant que la donnée charge.
const fmt = (n) => {
  if (n === null || n === undefined) return '…';
  if (n >= 1000) return (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + 'K';
  return String(n);
};

// Icône "scan" : un viseur (coins) traversé d'une ligne de scan -> lisible comme "scanner".
function ScanIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 8V6a2 2 0 0 1 2-2h2" />
      <path d="M16 4h2a2 2 0 0 1 2 2v2" />
      <path d="M20 16v2a2 2 0 0 1-2 2h-2" />
      <path d="M8 20H6a2 2 0 0 1-2-2v-2" />
      <line x1="4" y1="12" x2="20" y2="12" />
    </svg>
  );
}

function Stat({ value, label, href, tone, cta }) {
  return (
    <a className={`stat stat--link stat--${tone}`} href={href}>
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
      <span className="stat__cta">{cta} →</span>
    </a>
  );
}

function Landing() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const error = useSelector((s) => s.scan.error);
  const [stats, setStats] = useState(null);
  const [profileInput, setProfileInput] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveErr, setResolveErr] = useState(''); // '' | 'format' | 'notfound'
  const [showHelp, setShowHelp] = useState(false);
  const inputRef = useRef(null);

  // Chiffres en direct depuis la base de risque + rafraîchissement auto (imports n8n, etc.).
  useEffect(() => {
    let alive = true;
    const fetchStats = () =>
      getStats()
        .then(({ data }) => {
          if (alive) setStats(data);
        })
        .catch(() => {
          /* pas de stats -> les tuiles affichent "…" */
        });

    fetchStats();
    const id = setInterval(fetchStats, 30000); // re-check toutes les 30 s
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // Scan SANS connexion : on résout le SteamID depuis le lien/pseudo puis on lance le scan.
  const scanByProfile = async (e) => {
    e.preventDefault();
    if (resolving) return;
    const val = profileInput.trim();
    if (!val) { inputRef.current?.focus(); return; } // champ vide -> on invite à saisir
    // Pré-contrôle format : un SteamID numérique DOIT faire pile 17 chiffres.
    if (/^\d+$/.test(val) && val.length !== 17) {
      setResolveErr('format');
      inputRef.current?.focus();
      return;
    }
    setResolving(true);
    setResolveErr('');
    try {
      const { data } = await resolveSteamId(val); // le serveur vérifie que le compte existe
      // On ne compte le clic dans le funnel QUE si le SteamID est valide et que le scan part réellement.
      trackEvent('steam_click');
      storeSteamId(data.steamId);
      dispatch(startScan(data.steamId, i18n.resolvedLanguage));
    } catch {
      setResolveErr('notfound'); // 404 = aucun compte Steam ne correspond
      setResolving(false);
    }
  };

  // Démo sans compte Steam : lance le scan sur une bibliothèque d'exemple.
  const demo = () => {
    trackEvent('demo_start');
    storeSteamId('DEMO');
    dispatch(startScan('DEMO', i18n.resolvedLanguage));
  };

  return (
    <section className="landing">
      <div className="landing__glow" aria-hidden="true" />

      <p className="landing__eyebrow">{t('landing.eyebrow')}</p>
      <h1 className="landing__title">
        <span>{t('landing.title_1')}</span>
        <span className="landing__title-accent">{t('landing.title_2')}</span>
      </h1>
      <p className="landing__subtitle">{t('landing.subtitle')}</p>

      {error && <p className="landing__error">{t('landing.error')}</p>}

      {/* CTA principal : scan par SteamID, SANS connexion Steam.
          Les gens se méfient de connecter leur compte à un site inconnu (retours communautaires). */}
      <form className="landing__scan" onSubmit={scanByProfile}>
        <div className="landing__scan-row">
          <input
            ref={inputRef}
            type="text"
            className="landing__scan-input"
            placeholder={t('landing.profile_placeholder')}
            value={profileInput}
            onChange={(e) => { setProfileInput(e.target.value.replace(/\D/g, '')); setResolveErr(''); }}
            aria-label={t('landing.profile_placeholder')}
            inputMode="numeric"
            maxLength={17}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck="false"
          />
          <button
            type="submit"
            className={`landing__scan-btn${resolving ? ' is-loading' : ''}`}
            disabled={resolving || !profileInput.trim()}
            aria-label={t('landing.cta')}
            title={t('landing.cta')}
          >
            {resolving ? (
              <span className="landing__scan-spin" aria-hidden="true" />
            ) : (
              <>
                <ScanIcon />
                <span className="landing__scan-btn-label">{t('landing.scan_btn')}</span>
              </>
            )}
          </button>
        </div>
        {resolveErr && (
          <p className="landing__scan-err" role="alert">
            <span className="landing__scan-err-icon" aria-hidden="true">⚠</span>
            {t(resolveErr === 'format' ? 'landing.profile_err_format' : 'landing.profile_err_notfound')}
          </p>
        )}

        <button
          type="button"
          className={`landing__help-btn${showHelp ? ' is-open' : ''}`}
          onClick={() => setShowHelp((v) => !v)}
          aria-expanded={showHelp}
        >
          <span className="landing__help-btn-icon" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.5" y2="16.5" />
            </svg>
          </span>
          {t('landing.profile_help_btn')}
          <span className="landing__help-btn-chevron" aria-hidden="true">▾</span>
        </button>

        {showHelp && (
          <div className="landing__help">
            <div className="landing__help-cols">
              <div className="landing__help-col">
                <div className="landing__help-col-head">
                  <span className="landing__help-col-icon" aria-hidden="true">🖥️</span>
                  {t('landing.profile_help_pc_title')}
                </div>
                <p className="landing__help-col-body">{t('landing.profile_help_pc_body')}</p>
              </div>
              <div className="landing__help-col">
                <div className="landing__help-col-head">
                  <span className="landing__help-col-icon" aria-hidden="true">📱</span>
                  {t('landing.profile_help_mobile_title')}
                </div>
                <p className="landing__help-col-body">{t('landing.profile_help_mobile_body')}</p>
              </div>
            </div>
            <p className="landing__help-footer">
              <span aria-hidden="true">🔓</span> {t('landing.profile_help_footer')}
            </p>
          </div>
        )}
      </form>

      <p className="landing__reassure">
        <svg className="landing__reassure-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4" y="11" width="16" height="9" rx="2" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        </svg>
        {t('landing.reassure')}
      </p>

      <div className="landing__cta-row">
        <button type="button" className="landing__cta-demo" onClick={demo}>
          ▶ {t('landing.demo_cta')}
        </button>
      </div>

      <div className="landing__stats">
        <Stat value={fmt(stats?.total)} label={t('landing.stat_danger')} href="/games?type=danger" tone="danger" cta={t('landing.stat_cta')} />
        <Stat value={fmt(stats?.delisted)} label={t('landing.stat_delisted')} href="/games?type=delisted" tone="critical" cta={t('landing.stat_cta')} />
        <Stat value={fmt(stats?.savable)} label={t('landing.stat_savable')} href="/games?type=safe" tone="safe" cta={t('landing.stat_cta')} />
      </div>

      <Reviews />
    </section>
  );
}

export default Landing;
